import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PlanWeekendCards } from '../meal-prep/PlanCards'
import { describeProblem } from './planProblems'
import PreviewMenuEditor from './PreviewMenuEditor'

const EXPECTED_SECONDS = 90

function useElapsedSeconds(startedAt, running) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!running) return undefined
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [running])
  const started = startedAt ? Date.parse(startedAt) : NaN
  return Number.isNaN(started) ? 0 : Math.max(0, Math.round((now - started) / 1000))
}

function Problems({ problems }) {
  const { t } = useTranslation()
  if (!problems?.length) return null
  return (
    <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" role="alert" data-testid="preview-problems">
      <p className="font-medium">{t('weekPlanning.preview.needsReview')}</p>
      <ul className="list-disc pl-5">
        {problems.map((code) => {
          const described = describeProblem(code)
          return <li key={code}>{described ? t(`weekPlanning.problem.${described.key}`, described.values) : <span className="font-mono text-xs">{code}</span>}</li>
        })}
      </ul>
    </div>
  )
}

// VH-229 refinement: a plan under review — progress while it is written, then editable cards, a change
// request that writes it again, and "Übernehmen" / "Verwerfen". The week's plan stays until accepted.
export function WeekPlanPreview({ preview, hasDocument, actions, language }) {
  const { t } = useTranslation()
  const [instruction, setInstruction] = useState('')
  const generating = preview.state === 'generating'
  const elapsed = useElapsedSeconds(preview.started_at, generating)
  const doc = preview.document
  const busy = actions.edit.isPending || actions.accept.isPending || actions.discard.isPending || actions.generate.isPending

  if (generating) {
    const pct = Math.min(95, Math.round((elapsed / EXPECTED_SECONDS) * 100))
    return (
      <div className="rounded-lg border border-purple-200 bg-purple-50 p-4 space-y-3" role="status" data-testid="preview-generating">
        <p className="text-sm font-medium text-purple-900">{t('weekPlanning.preview.generating')}</p>
        <progress className="w-full h-2 accent-purple-600" max={100} value={pct} aria-label={t('weekPlanning.preview.generating')} />
        <p className="text-xs text-purple-900">
          {t('weekPlanning.preview.elapsed', { seconds: elapsed })}
          {preview.instruction ? ` · ${t('weekPlanning.preview.withInstruction', { instruction: preview.instruction })}` : ''}
        </p>
        <p className="text-xs text-gray-600">{t('weekPlanning.preview.generatingHint')}</p>
        <button type="button" className="btn-outline text-sm" disabled={busy} onClick={() => actions.discard.mutate()}>
          {t('weekPlanning.preview.cancel')}
        </button>
      </div>
    )
  }

  if (preview.state === 'failed' || !doc) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 space-y-3" role="alert" data-testid="preview-failed">
        <p className="text-sm text-red-800">{t(preview.error === 'timeout' ? 'weekPlanning.preview.timeout' : 'weekPlanning.preview.failed')}</p>
        <div className="flex flex-wrap gap-2">
          <button type="button" className="btn-primary text-sm" disabled={busy}
            onClick={() => actions.generate.mutate({ language, instruction: preview.instruction })}>
            {t('weekPlanning.preview.retry')}
          </button>
          <button type="button" className="btn-outline text-sm" disabled={busy} onClick={() => actions.discard.mutate()}>
            {t('weekPlanning.preview.discard')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="rounded-lg border-2 border-purple-300 p-3 sm:p-4 space-y-4" data-testid="preview-ready">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h5 className="text-base font-semibold text-gray-900">{t('weekPlanning.preview.title')}</h5>
          <p className="text-xs text-gray-600">
            {hasDocument ? t('weekPlanning.preview.replacesNote') : t('weekPlanning.preview.notSavedNote')}
            {doc.source === 'fallback' ? ` ${t('weeklyPlan.source.fallback')}` : ''}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" className="btn-primary text-sm" disabled={busy} onClick={() => actions.accept.mutate()}
            data-testid="accept-preview">
            {actions.accept.isPending ? t('weekPlanning.preview.accepting') : t('weekPlanning.preview.accept')}
          </button>
          <button type="button" className="btn-outline text-sm" disabled={busy} onClick={() => actions.discard.mutate()}
            data-testid="discard-preview">
            {t('weekPlanning.preview.discard')}
          </button>
        </div>
      </div>
      <Problems problems={doc.problems} />
      <p className="text-xs text-gray-600">{t('weekPlanning.preview.editHint')}</p>
      {actions.edit.isPending && <p className="text-xs text-gray-500" role="status">{t('weekPlanning.preview.recomputing')}</p>}
      <div className="rounded-lg bg-[#F6F7F4] p-3 space-y-5">
        <PreviewMenuEditor document={doc} disabled={busy} onEdit={(edits) => actions.edit.mutate(edits)}
          cycleId={preview.cycle_id} planId={preview.plan_id} />
        {((doc.weekend?.days ?? []).length > 0 || (doc.weekend?.rows ?? []).length > 0) && <PlanWeekendCards document={doc} />}
      </div>
      <div className="flex flex-col gap-2">
        <label className="text-sm font-medium text-gray-800" htmlFor={`instruction-${preview.plan_id}`}>
          {t('weekPlanning.preview.instruction')}
        </label>
        <textarea id={`instruction-${preview.plan_id}`} className="form-input text-sm" rows={2} maxLength={1000}
          value={instruction} placeholder={t('weekPlanning.preview.instructionPlaceholder')}
          onChange={(e) => setInstruction(e.target.value)} data-testid="preview-instruction" />
        <div>
          <button type="button" className="btn-outline text-sm" disabled={busy || !instruction.trim()}
            onClick={() => actions.generate.mutate({ language, instruction: instruction.trim() })} data-testid="regenerate-with-instruction">
            {t('weekPlanning.preview.regenerate')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default WeekPlanPreview
